import React from "react";

import cx from "classnames";

import { coalesce, objectKeys } from "@kazoohr/helpers";

import { Icon } from "../Icon";
import { BADGE_ICONS, BadgeIcon } from "../Icon/badgeIcons";

import styles from "./Badge.scss";
import { BADGE_SHAPES, BadgeShape } from "./shapes";

export type { BadgeShape };
export { BADGE_ICONS };
export type { BadgeIcon };

export type BadgeVariant = "duotone" | "flat";

export const BADGE_SHAPE_NAMES = objectKeys(BADGE_SHAPES);

// The inner circle spans 60% of the badge; the icon is sized to sit
// comfortably inside it.
const ICON_SCALE = 0.42;

export interface BadgeProps {
  /**
   * User-defined color used for the lighter (left) half of the badge. The
   * darker (right) half is this color mixed 65% with black.
   */
  primaryColor: string;
  /**
   * The outer shape of the badge.
   *
   * Default: `square`
   */
  shape?: BadgeShape;
  /**
   * Color treatment: `duotone` splits each half light/dark, `flat` uses a
   * single solid tone.
   *
   * Default: `duotone`
   */
  variant?: BadgeVariant;
  /**
   * Width/height of the (square) badge, in pixels.
   *
   * Default: `200`
   */
  size?: number;
  /**
   * Which design-provided icon to render in the center of the badge.
   *
   * Default: `badge_star`
   */
  icon?: BadgeIcon;
  /**
   * Additional CSS class names
   */
  className?: string;
  /**
   * Test ID for the badge
   */
  "data-testid"?: string;
}

export function Badge({
  primaryColor,
  shape = "square",
  variant = "duotone",
  size = 200,
  icon = "badge_star",
  className,
  "data-testid": dataTestId,
}: BadgeProps) {
  const { Artwork, iconCenter } = coalesce(
    BADGE_SHAPES[shape],
    BADGE_SHAPES.square
  );
  const iconSize = Math.round(size * ICON_SCALE);
  // Lift the icon onto the inner circle, which some shapes raise above center.
  const iconOffsetY = Math.round(size * (iconCenter - 0.5));

  return (
    <div
      className={cx(styles.badge, variant === "flat" && styles.flat, className)}
      data-testid={dataTestId}
      style={{ "--badge-primary-color": primaryColor } as React.CSSProperties}
    >
      <svg
        className={styles.artwork}
        viewBox="0 0 200 200"
        width={size}
        height={size}
        aria-hidden
      >
        <Artwork />
      </svg>
      <div
        className={styles.icon}
        style={{ transform: `translateY(${iconOffsetY}px)` }}
      >
        <Icon
          icon={icon}
          color="#fff"
          size={iconSize}
          decorative
          variant="filled"
        />
      </div>
    </div>
  );
}
